import React, { memo, useMemo, useContext, useEffect } from 'react';
import classnames from 'classnames';
import moment from 'moment';
import { isEqual } from 'lodash-es';
import { useRequest, useMemoizedFn } from 'ahooks';
import { H3 } from 'tea-component';
import { context as Context } from '@gp/components/Context';
import NoData from '@gp/components/NoData';
import TriggerViewCard from '@gp/components/TriggerViewCard';
import BarLineEchartCon from '../RevenueProfitCompletionBarLineEChartCon';
import MergeableTable from './components/MergeableTable';
import GeneralDownloadBtn from '@gp/components/GeneralDownloadBtn';
import useDownload, { createDownloadService } from '@gp/hooks/useDownload';
import {
  queryRevenueCompletionBarLineDataOfFinance,
  queryAllRevenueCompletionBarLineDataOfFinance,
  downloadRevenueCompletionOfFinance,
} from '@gp/pages/StudioDashboard/StudioOverall/services/studioOverallDetail';
import { getFirstDayOfYear, getLastDayOfYear } from '@gp/utils/common';
import { getStudioModuleName } from '../../../../../utils';
import {
  getBudgetActualForecastCompleteData,
  getBudgetActualForecastCompleteTableData,
  isBudgetActualOrForecastCompleteNoData,
  isRevenueCompletionTableNoData,
} from '../../utils';
import { FILE_TYPES1, FILE_TYPES_MAP } from '@gp/constants/const';
import './index.scss';

// Revenue Completion模块名称
const MODULE_NAME = 'Gross Revenue Completion';

// Revenue Completion模块 PDF下载 需要用到的className
const PDF_DOWNLOAD_NODE_CLASSNAME =
  'dbg-studio-overall-details-finance-revenue-completion-pdf-card';

interface RevenueCompletionProps extends IBaseProps {
  filter: IStudioOverallDetailsFilter;
}

function RevenueCompletion(props: RevenueCompletionProps) {
  const { filter, style, className } = props;

  const { studioId, studioName } =
    useContext<StudioOverallDetailContextTypes>(Context) || {};

  const {
    data: revenueCompletionData,
    loading: revenueCompletionLoading,
    run: revenueCompletionRun,
  } = useRequest(queryRevenueCompletionBarLineDataOfFinance, { manual: true });

  const revenueCompletionTrendsData = useMemo<
    IBudgetActualOrForecastCompleteData[]
  >(() => {
    const trendsData = revenueCompletionData?.trend;
    if (!Array.isArray(trendsData)) return [];
    return getBudgetActualForecastCompleteData(trendsData);
  }, [revenueCompletionData?.trend]);

  const revenueCompletionTrendsDataWithoutYTM = useMemo<
    IBudgetActualOrForecastCompleteData[]
  >(() => {
    if (!Array.isArray(revenueCompletionTrendsData)) return [];
    // 柱线图过滤掉YTM的数据
    return revenueCompletionTrendsData?.filter(
      (item) => item && moment(item?.date).isValid(),
    );
  }, [revenueCompletionTrendsData]);

  const revenueCompletionTablesData = useMemo<
    IBudgetActualOrForecastCompleteTableData[]
  >(() => {
    const tablesData = revenueCompletionData?.table;
    if (!Array.isArray(tablesData)) return [];
    return getBudgetActualForecastCompleteTableData(tablesData);
  }, [revenueCompletionData?.table]);

  const revenueCompletionTablesTotal = useMemo<number>(() => {
    // @ts-ignore
    return Number.parseInt(revenueCompletionData?.total, 10);
  }, [revenueCompletionData?.total]);

  const {
    data: allRevenueCompletionData,
    loading: allRevenueCompletionLoading,
    runAsync: allRevenueCompletionRun,
  } = useRequest(queryAllRevenueCompletionBarLineDataOfFinance, {
    manual: true,
  });

  const allRevenueCompletionTablesData = useMemo<
    IBudgetActualOrForecastCompleteTableData[]
  >(() => {
    const tablesData = allRevenueCompletionData?.table;
    if (!Array.isArray(tablesData)) return [];
    return getBudgetActualForecastCompleteTableData(tablesData);
  }, [allRevenueCompletionData?.table]);

  const allRevenueCompletionTablesTotal = useMemo<number>(() => {
    // @ts-ignore
    return Number.parseInt(allRevenueCompletionData?.total, 10);
  }, [allRevenueCompletionData?.total]);

  const { isDownloading, setIsDownloading, handleDownload } = useDownload({
    fileName: getStudioModuleName(MODULE_NAME, studioName),
    csvDownloadLogKey:
      'STUDIO_OVERALL_DETAIL_FINANCE_REVENUE_COMPLETION_CSV_SUBMIT',
    pdfDownloadLogKey:
      'STUDIO_OVERALL_DETAIL_FINANCE_REVENUE_COMPLETION_PDF_SUBMIT',
    node: document.querySelector(`.${PDF_DOWNLOAD_NODE_CLASSNAME}`),
    downloadService: createDownloadService(downloadRevenueCompletionOfFinance),
  });

  const handleDownloadInComp = useMemoizedFn(async (payload) => {
    if (isDownloading) return;
    const params = {
      ...payload,
      studio_id: studioId,
      start_date: getFirstDayOfYear(filter?.date),
      end_date: getLastDayOfYear(filter?.date),
      file_type: Number.parseInt(payload?.fileType, 10),
    };
    if (params.fileType === FILE_TYPES_MAP.PDF) {
      setIsDownloading?.(true);
      await allRevenueCompletionRun(params);
      setIsDownloading?.(false);
    }
    handleDownload?.(params);
  });

  // 筛选器变化，调接口刷新页面
  useEffect(() => {
    if (!studioId) return;
    if (!filter?.date) return;
    const params = {
      studio_id: studioId,
      start_date: getFirstDayOfYear(filter?.date),
      end_date: getLastDayOfYear(filter?.date),
    };
    revenueCompletionRun(params);
  }, [studioId, filter?.date]);

  const tabs = useMemo(() => {
    if (revenueCompletionLoading) return [];
    const isNoData =
      isBudgetActualOrForecastCompleteNoData(
        revenueCompletionTrendsDataWithoutYTM,
      ) && isRevenueCompletionTableNoData(revenueCompletionTablesData);
    return [
      {
        id: 'download',
        label: (
          <GeneralDownloadBtn
            uiType="2"
            fileTypes={FILE_TYPES1}
            isLoading={isDownloading}
            onConfirm={handleDownloadInComp}
          />
        ),
        component: isNoData ? (
          <NoData height={200} constomStyle={{ fontSize: 14 }} />
        ) : (
          <>
            <BarLineEchartCon data={revenueCompletionTrendsDataWithoutYTM} />
            <MergeableTable
              module="revenue"
              loading={revenueCompletionLoading}
              date={filter?.date}
              total={revenueCompletionTablesTotal}
              data={revenueCompletionTablesData}
            />
          </>
        ),
      },
    ];
  }, [
    revenueCompletionLoading,
    isDownloading,
    handleDownloadInComp,
    revenueCompletionTrendsDataWithoutYTM,
    filter?.date,
    revenueCompletionTablesTotal,
    revenueCompletionTablesData,
  ]);

  const pdfTabs = useMemo(() => {
    if (allRevenueCompletionLoading) return [];
    return [
      {
        id: 'table',
        label: null,
        component: (
          <MergeableTable
            module="revenue"
            loading={allRevenueCompletionLoading}
            date={filter?.date}
            total={allRevenueCompletionTablesTotal}
            data={allRevenueCompletionTablesData}
          />
        ),
      },
    ];
  }, [
    allRevenueCompletionLoading,
    filter?.date,
    allRevenueCompletionTablesTotal,
    allRevenueCompletionTablesData,
  ]);

  return (
    <div
      style={style}
      className={classnames(
        'tea-card dbg-studio-overall-details-finance-revenue-completion-card-container',
        className,
      )}
    >
      <TriggerViewCard
        className="dbg-studio-overall-details-finance-revenue-completion-content"
        title={<H3>{MODULE_NAME}</H3>}
        loading={revenueCompletionLoading}
        isNoData={false}
        tabs={tabs}
        defaultActiveId="download"
      />
      <TriggerViewCard
        className="dbg-studio-overall-details-finance-revenue-completion-content"
        // 这个rootNodeClassName 是为了 下载PDF用的 不要删除
        rootNodeClassName={PDF_DOWNLOAD_NODE_CLASSNAME}
        rootNodeStyle={{ marginTop: 0 }}
        title={<H3>{MODULE_NAME}</H3>}
        loading={revenueCompletionLoading}
        isNoData={false}
        tabs={pdfTabs}
        defaultActiveId="table"
      />
    </div>
  );
}

RevenueCompletion.defaultProps = {};

export default memo(RevenueCompletion, (prevProps: any, nextProps: any) => {
  return isEqual(prevProps, nextProps);
});
